/**
 * The signed-out shell: the forms that establish the session the plugin boot
 * needs. It is shell-own code because the plugin bundles it would otherwise use
 * are exactly what cannot load before a session exists (shell self-sufficiency
 * rule), and it uses no plugin primitives for the same reason.
 *
 * Four modes share one card: sign in, create the first administrator of an
 * empty deployment, register with an invitation or with a code delivered to a
 * phone number, and reset a forgotten password through a delivered code. Which
 * of the two registration forms a caller sees is the deployment's own answer
 * (`/auth/state`), never a build-time flag: a deployment that mounts a delivery
 * provider refuses a registration with no proven phone, so the card collects the
 * code there and nowhere else.
 */
import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import {
  bootstrapAdmin, registerAccount, requestCode, resetPassword, signIn,
} from './auth-api.ts'
import type { CodePurpose } from './auth-api.ts'
import css from './AuthSurface.module.css'

/** Which form the card shows. */
type Mode = 'bootstrap' | 'signin' | 'register' | 'reset'

/** Field labels per mode, kept beside the form they head. */
const TITLE: Readonly<Record<Mode, string>> = {
  bootstrap: '创建管理员账号',
  signin: '登录',
  register: '邀请码注册',
  reset: '重置密码',
}

/** One line of orientation per mode. */
const HINT: Readonly<Record<Mode, string>> = {
  bootstrap: '本部署尚未创建账号。请创建第一个管理员，之后由管理员为审核员发放邀请码。',
  signin: '请输入账号与密码。',
  register: '请填写管理员发放的邀请码，注册后即可登录。',
  reset: '请使用注册手机号接收验证码，然后设置新密码。',
}

/** The registration form of a deployment that requires a verified phone number. */
const VERIFIED_REGISTER = {
  title: '手机号注册',
  hint: '请填写手机号并获取短信验证码完成注册；持有邀请码时也可直接填写邀请码完成注册。',
} as const

/** Submit-button copy per mode. */
const SUBMIT: Readonly<Record<Mode, string>> = {
  bootstrap: '创建并登录',
  signin: '登录',
  register: '注册',
  reset: '设置新密码',
}

/** Props of the signed-out shell. */
export interface AuthSurfaceProps {
  /** Whether the deployment has no account yet, so the first form creates the administrator. */
  needsBootstrap: boolean
  /**
   * Whether a registration has to prove its phone number with a delivered code.
   * The account surface answers this, so the card offers the code step exactly
   * where the store will hold a registration to it.
   */
  verificationRequired: boolean
  /** The interval, in seconds, the deployment refuses a second code inside. */
  codeResendSeconds: number
  /**
   * Why this card is being shown, when the browser was sent back here rather
   * than arriving on its own — a session the deployment refused. Absent on an
   * ordinary visit, and the caller reads it once (see `session-watch.ts`).
   */
  notice?: string
  /** Called once a session exists; the boot chain continues with the plugin boot. */
  onSignedIn: () => void
  /** Request implementation; defaults to the platform fetch. */
  fetchImpl?: typeof fetch
}

/**
 * The seconds left before another code request may be sent, and the restart that
 * begins the window the account service enforces. A second request inside the
 * interval is refused, so the card counts it down and keeps its button disabled
 * until it elapses rather than letting the caller discover the rule by refusal.
 * @returns the seconds left, and the restart a caller invokes after every
 * request the surface answered.
 */
function useResendCountdown(): { left: number; restart: (seconds: number) => void } {
  const [left, setLeft] = useState(0)
  useEffect(() => {
    if (left === 0) return
    const timer = setTimeout(() => { setLeft(current => current - 1) }, 1000)
    return () => { clearTimeout(timer) }
  }, [left])
  return { left, restart: (seconds: number) => { setLeft(Math.max(0, Math.trunc(seconds))) } }
}

/**
 * Render the signed-out shell.
 * @param props - the probe result, the continuation, and an optional fetch.
 * @returns the account card.
 */
export function AuthSurface(props: AuthSurfaceProps) {
  const fetchImpl = props.fetchImpl ?? globalThis.fetch.bind(globalThis)
  const [mode, setMode] = useState<Mode>(props.needsBootstrap ? 'bootstrap' : 'signin')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [realName, setRealName] = useState('')
  const [phone, setPhone] = useState('')
  const [invite, setInvite] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [notice, setNotice] = useState<string | undefined>(props.notice)
  const resend = useResendCountdown()

  /** Whether this registration form must present a code the deployment delivered. */
  const codeStep = props.verificationRequired && mode === 'register'
  /** Whether this form asks for a code at all: a reset, or a registration a provider closes. */
  const collectsCode = mode === 'reset' || codeStep

  /** Move to another form, dropping what the previous one reported. */
  const go = (next: Mode): void => {
    setMode(next)
    setError(undefined)
    setNotice(undefined)
    setPassword('')
    setConfirm('')
    setCode('')
    setCodeSent(false)
    resend.restart(0)
  }

  /** Validate the two password boxes of a mode that sets one. */
  const passwordProblem = (): string | undefined => {
    if (password === '') return '请填写密码'
    if (confirm !== password) return '两次输入的密码不一致'
    return undefined
  }

  /** What the active form still needs before it can be sent. */
  const formProblem = (): string | undefined => {
    if (mode === 'reset') {
      if (phone.trim() === '') return '请填写手机号'
      if (!codeSent) return '请先获取验证码'
      if (code.trim() === '') return '请填写验证码'
    }
    if (mode !== 'reset' && username.trim() === '') return '请填写账号'
    // A registration an invitation activates needs no code, which is the one way
    // into a deployment that otherwise requires a proven phone number.
    if (codeStep && invite.trim() === '') {
      if (phone.trim() === '') return '请填写手机号'
      if (!codeSent) return '请先获取验证码'
      if (code.trim() === '') return '请填写验证码'
    }
    return mode === 'signin' ? password === '' ? '请填写密码' : undefined : passwordProblem()
  }

  /** Run one submit for the active mode. */
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    if (busy) return
    setError(undefined)
    setNotice(undefined)
    const problem = formProblem()
    if (problem !== undefined) { setError(problem); return }

    setBusy(true)
    const credentials = {
      username: username.trim(),
      password,
      realName: realName.trim(),
      phone: phone.trim(),
      invite: invite.trim(),
      code: code.trim(),
    }
    if (mode === 'bootstrap') {
      const result = await bootstrapAdmin(fetchImpl, credentials)
      setBusy(false)
      if (!result.ok) { setError(result.message); return }
      props.onSignedIn()
      return
    }
    if (mode === 'signin') {
      const result = await signIn(fetchImpl, credentials)
      setBusy(false)
      if (!result.ok) { setError(result.message); return }
      props.onSignedIn()
      return
    }
    if (mode === 'register') {
      const result = await registerAccount(fetchImpl, credentials)
      setBusy(false)
      if (!result.ok) { setError(result.message); return }
      if (result.pending) {
        go('signin')
        setNotice('注册成功，请等待管理员启用账号后再登录。')
        return
      }
      props.onSignedIn()
      return
    }
    const result = await resetPassword(fetchImpl, { phone: phone.trim(), code: code.trim(), password })
    setBusy(false)
    if (!result.ok) { setError(result.message); return }
    go('signin')
    setNotice('密码已重置，请使用新密码登录。')
  }

  /**
   * Ask for a code for one purpose and report how it travelled.
   * @param purpose - what the code is being issued for.
   */
  const sendCode = async (purpose: CodePurpose): Promise<void> => {
    if (busy) return
    setError(undefined)
    setNotice(undefined)
    if (phone.trim() === '') { setError('请填写手机号'); return }
    setBusy(true)
    const result = await requestCode(fetchImpl, { phone: phone.trim(), purpose })
    setBusy(false)
    if (!result.ok) {
      setError(result.message)
      // A refused request the surface answered has still started the resend
      // window — including one whose message the gateway could not send — so the
      // button waits rather than sending the caller into the next refusal.
      if (result.answered) resend.restart(props.codeResendSeconds)
      return
    }
    setCodeSent(true)
    resend.restart(props.codeResendSeconds)
    setNotice(result.code === undefined
      ? '验证码已发送，请查看手机短信。'
      : `验证码：${result.code}（本部署未接入短信网关，请由管理员转达）`)
  }

  return (
    <div className={css.page}>
      <form className={css.card} onSubmit={(event) => { void submit(event) }}>
        <div className={css.brand}>AI 环保审核助手</div>
        <div className={css.title}>{codeStep ? VERIFIED_REGISTER.title : TITLE[mode]}</div>
        <div className={css.hint}>{codeStep ? VERIFIED_REGISTER.hint : HINT[mode]}</div>

        {mode === 'register' && (
          <label className={css.field}>
            <span className={css.label}>{codeStep ? '邀请码（可选）' : '邀请码'}</span>
            <input className={css.input} value={invite} onChange={(event) => { setInvite(event.target.value) }} />
          </label>
        )}

        {mode !== 'reset' && (
          <label className={css.field}>
            <span className={css.label}>账号</span>
            <input className={css.input} value={username} onChange={(event) => { setUsername(event.target.value) }} autoComplete="username" />
          </label>
        )}

        {(mode === 'bootstrap' || mode === 'register') && (
          <>
            <label className={css.field}>
              <span className={css.label}>姓名</span>
              <input className={css.input} value={realName} onChange={(event) => { setRealName(event.target.value) }} />
            </label>
            <label className={css.field}>
              <span className={css.label}>手机号</span>
              <input className={css.input} value={phone} onChange={(event) => { setPhone(event.target.value) }} />
            </label>
          </>
        )}

        {mode === 'reset' && (
          <label className={css.field}>
            <span className={css.label}>手机号</span>
            <input className={css.input} value={phone} onChange={(event) => { setPhone(event.target.value) }} />
          </label>
        )}

        {collectsCode && codeSent && (
          <label className={css.field}>
            <span className={css.label}>验证码</span>
            <input className={css.input} value={code} onChange={(event) => { setCode(event.target.value) }} />
          </label>
        )}

        {(mode !== 'reset' || codeSent) && (
          <label className={css.field}>
            <span className={css.label}>{mode === 'reset' ? '新密码' : '密码'}</span>
            <input
              className={css.input}
              type="password"
              value={password}
              onChange={(event) => { setPassword(event.target.value) }}
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            />
          </label>
        )}

        {(mode === 'bootstrap' || mode === 'register' || (mode === 'reset' && codeSent)) && (
          <label className={css.field}>
            <span className={css.label}>确认密码</span>
            <input
              className={css.input}
              type="password"
              value={confirm}
              onChange={(event) => { setConfirm(event.target.value) }}
              autoComplete="new-password"
            />
          </label>
        )}

        {error !== undefined && <div className={css.error} role="alert">{error}</div>}
        {notice !== undefined && <div className={css.notice}>{notice}</div>}

        <div className={css.actions}>
          {collectsCode && (
            <button
              className={css.secondary}
              type="button"
              disabled={busy || resend.left > 0}
              onClick={() => { void sendCode(mode === 'reset' ? 'reset-password' : 'register') }}
            >
              {resend.left > 0 ? `重新获取（${String(resend.left)} 秒）` : '获取验证码'}
            </button>
          )}
          <button className={css.submit} type="submit" disabled={busy}>{busy ? '提交中…' : SUBMIT[mode]}</button>
        </div>

        <div className={css.links}>
          {mode === 'signin' && (
            <>
              <button className={css.link} type="button" onClick={() => { go('register') }}>
                {props.verificationRequired ? '手机号注册' : '使用邀请码注册'}
              </button>
              <button className={css.link} type="button" onClick={() => { go('reset') }}>忘记密码</button>
            </>
          )}
          {mode !== 'signin' && !props.needsBootstrap && (
            <button className={css.link} type="button" onClick={() => { go('signin') }}>返回登录</button>
          )}
        </div>
      </form>
    </div>
  )
}
